import { ArrowSmallDownIcon, ArrowSmallUpIcon } from '@heroicons/react/20/solid';
import Currency from '../../../components/currency/Currency';
import { getVarianceDirectionColor } from '../../../utilities/variance-color';
import { useState } from 'react';
import { isNullOrUndefined } from '../../new-workpaper/utils';
import { formatPreviousPeriodEndDate } from '../../new-or-edit-journal/utils';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { formatDate } from '../../../utilities/formatting';
import { TooltipV2 } from '@cimplico/components';
import { Account } from '../../../types/account';
import ClosingAmountDiffWarning from '../../trial-balance/ClosingAmountDiffWarning';
import { WorksheetWithAccount } from '../../../types/worksheet';

type AccountFiguresProps = {
    account: Account;
    unreconciledCr?: number | null;
    unreconciledDr?: number | null;
    worksheet: WorksheetWithAccount;
};

function AccountFigures({ account, unreconciledCr, unreconciledDr, worksheet }: AccountFiguresProps): JSX.Element {
    const { periodEnd, entity } = useWorkpaperContext();
    const [showVarianceAsPercent, setShowVarianceAsPercent] = useState(true);

    let currentAmount = null;

    if (typeof account.workpaperDebitTY === 'number') {
        currentAmount = account.workpaperDebitTY;
    } else if (typeof account.workpaperCreditTY === 'number') {
        currentAmount = account.workpaperCreditTY * -1;
    }

    let ledgerTY = null;

    if (typeof account.ledgerDebitTY === 'number') {
        ledgerTY = account.ledgerDebitTY;
    } else if (typeof account.ledgerCreditTY === 'number') {
        ledgerTY = account.ledgerCreditTY * -1;
    }

    const amountLY = account.ledgerDebitLY ?? account.ledgerCreditLY;

    const unReconciledAmount = isNullOrUndefined(unreconciledDr) ? unreconciledCr : unreconciledDr;
    const reconciled = !unreconciledDr && !unreconciledCr;
    const lastYearPeriod = formatPreviousPeriodEndDate(new Date(periodEnd), entity!.financialYearEndMonth, entity!.financialYearEndDay);
    const difference = (currentAmount ?? 0) - (ledgerTY ?? 0);

    return (
        <div className="flex items-start justify-between gap-4 flex-wrap">
            {/* CURRENT AMOUNT */}
            <div className="grid grid-cols-[200px_auto] justify-start gap-4">
                <section className="text-left">
                    <label className="text-gray-500 text-sm">Current Amount</label>
                    <div className="flex gap-2">
                        <Currency
                            value={account.workpaperDebitTY ?? account.workpaperCreditTY}
                            className="text-indigo-700 font-medium"
                            withParentheses={Boolean(account.workpaperCreditTY)}
                        />
                        {/* VARIANCE */}
                        <TooltipV2 message="Click the variance figure to swap it between percentage and monetary change.">
                            <div
                                className="flex items-center cursor-pointer font-medium"
                                onClick={(): void => setShowVarianceAsPercent(!showVarianceAsPercent)}
                            >
                                <TrendArrow account={account} />
                                {showVarianceAsPercent ? (
                                    <p className="text-gray-500 text-sm">{Math.abs(account!.variancePercent).toFixed(1)}%</p>
                                ) : (
                                    <Currency value={Math.abs(account!.variance)} className="text-gray-500 text-sm" />
                                )}
                            </div>
                        </TooltipV2>
                    </div>
                    {/* DRAFT AMOUNTS */}
                    {difference !== 0 && (
                        <div className="mt-1">
                            <p className="flex items-center text-sm text-gray-500">
                                <Currency
                                    as={'span'}
                                    value={account.ledgerDebitTY ?? account.ledgerCreditTY}
                                    withParentheses={Boolean(account.ledgerCreditTY)}
                                    className="font-medium"
                                />
                                <span>&nbsp;in the ledger</span>
                            </p>
                            <p className="flex items-center text-sm text-gray-500 mt-1">
                                <Currency
                                    as={'span'}
                                    value={Math.abs(difference)}
                                    withParentheses={difference < 0}
                                    className="font-medium"
                                />
                                <span>&nbsp;of draft journals</span>
                            </p>
                        </div>
                    )}
                </section>
                {/* LAST YEAR AMOUNT */}
                <section className="text-right">
                    <div className="flex items-center relative">
                        <label className="text-gray-500 text-sm whitespace-nowrap">{formatDate(lastYearPeriod)} (LY)</label>
                        <div className="absolute left-full ml-2">
                            <ClosingAmountDiffWarning worksheet={worksheet} />
                        </div>
                    </div>
                    <Currency value={amountLY} className="text-gray-700 font-medium" withParentheses={Boolean(account.ledgerCreditLY)} />
                </section>
            </div>

            {/* UNRECONCILED AMOUNT */}
            {unReconciledAmount !== undefined && (
                <section className="text-right" data-intercom-target="worksheet-unreconciled-amount">
                    <label className="text-gray-500 text-sm font-bold">Unreconciled Amount</label>
                    <Currency
                        value={unReconciledAmount}
                        className={`${reconciled ? 'text-green-600 ' : 'text-red-600 '}`}
                        withParentheses={!isNullOrUndefined(unreconciledCr)}
                    />
                </section>
            )}
        </div>
    );
}

type TrendArrowProps = {
    account: Account;
};
function TrendArrow({ account }: TrendArrowProps): JSX.Element {
    return (
        <span className={`${getVarianceDirectionColor(account)}`}>
            {account.variance < 0 ? <ArrowSmallDownIcon className="w-5 h-5" /> : <ArrowSmallUpIcon className="w-5 h-5" />}
        </span>
    );
}

export default AccountFigures;
